/**
 * Credential transport for server-executed approval tools.
 *
 * Credentials harvested at approval-click travel ONLY as a request header —
 * `x-hitl-credentials: base64url(JSON.stringify({key: value}))` — attached exclusively on
 * turns that carry an approval response for a tool whose HITL module declares
 * `getCredentials`. They never enter messages, tool args, memory, or the LLM payload.
 */

import { encodeBase64Url } from '@/lib/utils/base64url';

export const HITL_CREDENTIALS_HEADER = 'x-hitl-credentials';

interface ApprovalPartShape {
  type?: string;
  state?: string;
  toolName?: string;
  approval?: { approved?: boolean };
}

interface MessageShape {
  role?: string;
  parts?: readonly ApprovalPartShape[];
}

function toolNameOfPart(part: ApprovalPartShape): string | undefined {
  if (part.toolName) return part.toolName;
  if (typeof part.type === 'string' && part.type.startsWith('tool-')) return part.type.slice(5);
  return undefined;
}

/**
 * True when the trailing assistant message carries a fresh approval response
 * (`state: 'approval-responded'`) for one of `toolNames` — i.e. this POST is the resume turn
 * that needs the credential header.
 */
export function turnCarriesApprovalResponseFor({
  messages,
  toolNames,
}: {
  messages: readonly MessageShape[];
  toolNames: ReadonlySet<string>;
}): boolean {
  if (toolNames.size === 0) return false;
  const last = messages[messages.length - 1];
  if (last?.role !== 'assistant') return false;
  return (last.parts ?? []).some(part => {
    if (part.state !== 'approval-responded') return false;
    if (typeof part.approval?.approved !== 'boolean') return false;
    const name = toolNameOfPart(part);
    return !!name && toolNames.has(name);
  });
}

export function encodeHitlCredentials(credentials: Record<string, string>): string {
  return encodeBase64Url(JSON.stringify(credentials));
}
